import { useState } from "react";
import { useFetch } from "@/hooks/useFetch";
import { PageHeader, Panel, Loading, StatusBadge, Tag } from "@/components/common";
import { Waveform, Play, Microphone, SlidersHorizontal } from "@phosphor-icons/react";
import { toast } from "sonner";

const EMOTIONS = ["Happy", "Excited", "Sad", "Scared", "Curious", "Whispering", "Confused", "Laughing"];
const LANGS = ["English", "Turkish", "Bulgarian", "Spanish", "German", "French"];

export default function VoiceStudio() {
  const { data: chars, loading } = useFetch("/characters");
  const [active, setActive] = useState(0);
  const [emotion, setEmotion] = useState("Excited");
  const [pitch, setPitch] = useState(50);
  const [speed, setSpeed] = useState(50);
  const [energy, setEnergy] = useState(65);
  if (loading || !chars) return <Loading />;
  const c = chars[active];

  return (
    <div>
      <PageHeader icon={Waveform} title="Voice Studio"
        subtitle="Each character has a permanent voice identity. Adjust pitch, speed, energy and emotion. Voice generation connects to the voice provider in Phase 2."
        badge={<StatusBadge status="NOT CONNECTED" />} />

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="space-y-2">
          {chars.map((ch, i) => (
            <button key={ch.id} data-testid={`voice-char-${ch.id}`} onClick={() => setActive(i)}
              className={`w-full flex items-center gap-3 rounded-xl border p-3 text-left transition-colors ${i === active ? "border-[#00E5FF]/50 bg-white/5" : "border-white/8 hover:border-white/20"}`}>
              <img src={ch.image} alt="" className="h-10 w-10 rounded-lg object-cover" />
              <div><div className="font-semibold text-white text-sm">{ch.name}</div><div className="text-[11px] text-zinc-500">{ch.voice}</div></div>
            </button>
          ))}
        </div>

        <div className="lg:col-span-2 space-y-5">
          <Panel>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2"><SlidersHorizontal size={16} weight="duotone" className="text-[#00E5FF]" /><span className="font-display font-bold text-white">{c.name} — Voice Identity</span></div>
              <button data-testid="voice-preview" onClick={() => toast("Phase 2: previews the character voice with the voice provider.")}
                className="inline-flex items-center gap-1.5 rounded-full bg-white/5 border border-white/10 px-3 py-1.5 text-xs text-white hover:border-[#00E5FF]/40 transition-colors"><Play size={13} weight="fill" /> Preview</button>
            </div>
            <div className="space-y-4">
              <Slider label="Pitch" value={pitch} onChange={setPitch} />
              <Slider label="Speed" value={speed} onChange={setSpeed} />
              <Slider label="Energy" value={energy} onChange={setEnergy} />
            </div>
            <div className="mt-5">
              <div className="text-xs text-zinc-500 mb-2">Emotion</div>
              <div className="flex flex-wrap gap-2">
                {EMOTIONS.map((e) => (
                  <button key={e} onClick={() => setEmotion(e)} className={`rounded-full px-3 py-1.5 text-xs border transition-colors ${emotion === e ? "border-[#FF007F]/60 bg-[#FF007F]/10 text-[#FF007F]" : "border-white/10 text-zinc-400 hover:border-white/25"}`}>{e}</button>
                ))}
              </div>
            </div>
          </Panel>

          <Panel>
            <div className="flex items-center gap-2 mb-3 text-sm text-zinc-400"><Microphone size={16} weight="duotone" className="text-[#00FF9D]" /> Performance reference</div>
            <p className="text-sm text-zinc-300">Perform a line yourself with the mic — Studio AI captures timing, pauses, energy and emphasis, then recreates it using the official character voice.</p>
            <p className="mt-2 text-xs text-[#FFB800]">Never clones a real person's voice without authorization.</p>
            <button onClick={() => toast("Phase 2: capture performance intention.")} className="mt-3 inline-flex items-center gap-2 rounded-full border border-white/10 bg-[#1C1C1C] px-4 py-2 text-sm text-white hover:border-white/30 transition-colors"><Microphone size={15} weight="duotone" /> Record Performance</button>
          </Panel>

          <Panel>
            <div className="text-sm text-zinc-400 mb-3">Multilingual voices</div>
            <div className="flex flex-wrap gap-2">{LANGS.map((l) => <Tag key={l}>{c.name} · {l}</Tag>)}</div>
            <p className="mt-3 text-xs text-zinc-500">Where the provider supports it, a consistent voice identity is preserved across languages — {c.name} still sounds like {c.name}.</p>
          </Panel>
        </div>
      </div>
    </div>
  );
}

function Slider({ label, value, onChange }) {
  return (
    <div>
      <div className="flex justify-between text-xs mb-1"><span className="text-zinc-400">{label}</span><span className="font-mono text-zinc-500">{value}</span></div>
      <input type="range" min="0" max="100" value={value} onChange={(e) => onChange(+e.target.value)}
        className="w-full accent-[#00E5FF]" data-testid={`slider-${label}`} />
    </div>
  );
}
